import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  paragraph: {
    marginBottom: 8
  },
  strong: {
    fontFamily: 'Times-Bold'
  },
  italic: {
    fontFamily: 'Times-Italic'
  },
  underline: {
    textDecoration: 'underline'
  },
  listItemContent: {
    marginLeft: 16,
    flexDirection: 'row',
    alignItems: 'center'
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  bullet: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: 'black',
    marginRight: 3
  },
  listNumber: {
    fontSize: 10,
    marginRight: 3,
    alignSelf: 'baseline',
    top: 0
  }
});

const InvoiceHTMLRenderer = ({ htmlText }) => {
  const getTextWidth = (text, font = '12pt Times') => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  };

  const parseText = (child, keyComponent) => {
    const { textContent } = child;
    const maxWidth = 20;

    const textSegments = [];
    let currentSegment = '';
    for (let i = 0; i < textContent.length; i += 1) {
      const currentText = currentSegment + textContent[i];
      if (getTextWidth(currentText) <= maxWidth) {
        currentSegment += textContent[i];
      } else {
        textSegments.push(currentSegment);
        currentSegment = textContent[i];
      }
    }

    if (currentSegment) {
      textSegments.push(currentSegment);
    }

    return textSegments.map((segment, idx) => (
      <Text key={`${keyComponent}-${idx}`} style={{ fontSize: 10 }}>{segment}</Text>
    ));
  };

  const parseHTML = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const children = Array.from(doc.body.childNodes);

    return children.map((child, index) => {
      const keyComponent = `component-${index}-id`;

      if (child.nodeType === Node.TEXT_NODE) {
        return parseText(child, keyComponent);
      }

      const tag = child.tagName.toLowerCase();
      const props = {};
      Array.from(child.attributes).forEach((attribute) => {
        props[attribute.nodeName] = attribute.nodeValue;
      });

      let count = 0;
      let style = {};
      switch (tag) {
        case 'strong':
          style = styles.strong;
          break;
        case 'em':
          style = styles.italic;
          break;
        case 'u':
          style = styles.underline;
          break;
        case 'ul':
          style = styles.paragraph;
          return (
            <View key={keyComponent} style={style}>
              {parseHTML(child.innerHTML).map((item, i) => (
                <View key={`ul-item-${i}`} style={styles.listItemContent}>
                  <View style={styles.bullet} />
                  {item}
                </View>
              ))}
            </View>
          );
        case 'ol':
          style = styles.paragraph;
          return (
            <View key={keyComponent} style={style}>
              {parseHTML(child.innerHTML).map((item, i) => (
                <View key={`ol-item-${i}`} style={styles.listItemContent}>
                  <Text style={styles.listNumber}>{`${++count}.`}</Text>
                  {item}
                </View>
              ))}
            </View>
          );
        case 'li':
          style = styles.listItem;
          return (
            <View key={keyComponent} style={style}>
              {parseHTML(child.innerHTML)}
            </View>
          );
        default:
          break;
      }

      const reactProps = { key: keyComponent, style, ...props };

      return React.createElement(Text, reactProps, parseHTML(child.innerHTML));
    });
  };

  return (
    <View style={styles.paragraph}>
      {parseHTML(htmlText)}
    </View>
  );
};

export default InvoiceHTMLRenderer;
